import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaPlus, FaGraduationCap, FaBookOpen, FaTrophy } from "react-icons/fa";
import { fetchAllCourses } from "../../apis/CourseApis";
import { CourseCard } from "./CourseCard";
import Leaderboard from "./Leaderboard";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import SeoMeta from "../SeoMeta";
import { SkeletonCourseGrid } from "../Skeletons";
import { useTheme } from "../Theme/ThemeContext";

export default function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeaderboard, setShowLeaderboard] = useState(true);

  const { user, loggedInUser } = useAuth();
  const { errorToast } = useToastHelpers();
  const { currentTheme } = useTheme();

  const STATIC_LOCKED_COURSES = [
    {
      id: "static-go-dsa",
      title: "Go + Data Structures & Algorithms",
      description: "Problem Solving Mastery\nMaster data structures, algorithmic thinking, and Go performance.",
      isPremium: true,
      isLocked: true,
      isStatic: true,
    },
    {
      id: "static-advanced-go",
      title: "Advanced Go + Project",
      description: "Concurrency, APIs & Production Systems\nBuild scalable, concurrent, productionready Go applications.",
      isPremium: true,
      isLocked: true,
      isStatic: true,
    },
    {
      id: "static-mysql",
      title: "Database- Mysql",
      description: "Learn about MySQL database basics and joins and queries",
      isPremium: true,
      isLocked: true,
      isStatic: true,
    },
    {
      id: "static-mongodb",
      title: "Database- MongoDb",
      description: "Learn about mongodb database basics and agregation and queries",
      isPremium: true,
      isLocked: true,
      isStatic: true,
    },
  ];

  const mergeUniqueById = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = String(item?.id ?? "");
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Fetch courses
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    
    const loadCourses = async () => {
      try {
        const result = await fetchAllCourses();
        if (isMounted) {
          let normalized = [];
          if (Array.isArray(result?.courses)) {
            normalized = result.courses;
          } else if (Array.isArray(result)) {
            normalized = result;
          }

          const merged = mergeUniqueById([...(normalized || []), ...STATIC_LOCKED_COURSES]);
          setCourses(merged);
        }
      } catch (err) {
        console.error("Error loading courses:", err);
        if (isMounted) errorToast("Failed to load courses");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    loadCourses();
    return () => { isMounted = false; };
  }, []);

  const handleCourseDeleted = (courseId) => {
    setCourses((prev) => prev.filter((c) => c.id !== courseId));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-accent-50/30 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
      <SeoMeta
        title="Courses - Code Arena"
        description="Learn programming through structured courses with chapters and topics"
        url="https://www.code-arena.in/courses"
        image="/logo192.png"
      />

      {/* Hero Section */}
      <div className="relative py-16 overflow-hidden" style={{ background: `linear-gradient(135deg, rgb(var(--color-primary)) 0%, rgb(var(--color-secondary)) 50%, rgb(var(--color-accent)) 100%)` }}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full filter blur-3xl" style={{ background: `rgba(var(--color-accent), 0.2)` }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-4">
                <FaGraduationCap className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-medium">Learning Hub</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
                Explore Our Courses
              </h1>
              <p className="text-white/80 text-lg max-w-xl">
                Master programming through structured courses with comprehensive theory, videos, and hands-on practice
              </p>
            </div>

            {/* Admin Add Button */}
            {loggedInUser?.role === "admin" && (
              <Link
                to="/courses/new"
                className={`inline-flex items-center gap-2 bg-white text-theme-primary font-semibold py-3 px-6 rounded-xl shadow-lg shadow-theme hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
              >
                <FaPlus className="w-4 h-4" />
                Add Course
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Courses (Primary) */}
          <div className="flex-1 min-w-0">
            {/* Header with Results Count */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-xl flex items-center justify-center`}>
                  <FaBookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-neutral-600 dark:text-neutral-400 text-sm">Available Courses</p>
                  <p className="font-bold text-neutral-900 dark:text-white text-lg">{courses.length} Course{courses.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              
              {/* Leaderboard Toggle Button (Mobile/Tablet) */}
              {!showLeaderboard && (
                <button
                  onClick={() => setShowLeaderboard(true)}
                  className={`lg:hidden inline-flex items-center gap-2 bg-gradient-to-r ${currentTheme.colors.gradient} text-white font-semibold py-2 px-4 rounded-xl shadow-lg ${currentTheme.colors.glow} hover:shadow-xl transition-all duration-300`}
                >
                  <FaTrophy className="w-4 h-4" />
                  <span className="hidden sm:inline">Leaderboard</span>
                </button>
              )}
            </div>

            {/* Loading State */}
            {loading && <SkeletonCourseGrid count={6} />}

            {/* Empty State */}
            {!loading && courses.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-gradient-to-br from-neutral-100 to-neutral-200 dark:from-neutral-800 dark:to-neutral-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaBookOpen className="w-10 h-10 text-neutral-400 dark:text-neutral-500" />
                </div>
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
                  No courses found
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                  Check back soon for new courses!
                </p>
                {loggedInUser?.role === "admin" && (
                  <Link
                    to="/courses/new"
                    className={`inline-flex items-center gap-2 bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 text-white font-semibold py-3 px-6 rounded-xl shadow-lg ${currentTheme.colors.glow} hover:shadow-xl transition-all duration-300`}
                  >
                    <FaPlus className="w-4 h-4" />
                    Create First Course
                  </Link>
                )}
              </div>
            )}

            {/* Courses Grid */}
            {!loading && courses.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {courses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    user={user}
                    onCourseDeleted={handleCourseDeleted}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Leaderboard Sidebar */}
          {showLeaderboard && (
            <div className="w-full lg:w-[360px] flex-shrink-0">
              <Leaderboard onClose={() => setShowLeaderboard(false)} />
            </div>
          )}

          {/* Show Leaderboard Button (Desktop - when hidden) */}
          {!showLeaderboard && (
            <div className="hidden lg:block w-[360px] flex-shrink-0">
              <button
                onClick={() => setShowLeaderboard(true)}
                className={`w-full flex items-center justify-center gap-3 bg-gradient-to-r ${currentTheme.colors.gradient} text-white font-semibold py-4 px-6 rounded-2xl shadow-lg ${currentTheme.colors.glow} hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
              >
                <FaTrophy className="w-5 h-5" />
                Show Leaderboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}